import React from "react";
import type { ApolloClient } from "@apollo/client";
import { ISOLVED_WHOLESALE, LEGACY_NOBILL, STRIPE } from "statics/billingTypes";
import { Col, Form } from "react-bootstrap";
import { Formik } from "formik";
import { map } from "lodash";
import Button from "components/Button";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { changeBillingType } from "actions/adminActions";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { toast } from "react-toastify";
import Alert from "components/Alert";
import type { RootState } from "types/state";

const providers = [ISOLVED_WHOLESALE, LEGACY_NOBILL, STRIPE];

interface FormValues {
  billingType: string;
}

const errorSelector = createErrorSelector([
  adminConstants.CHANGE_BILLING_TYPES,
]);
const loadingSelector = createLoadingSelector(
  adminConstants.CHANGE_BILLING_TYPES
);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isSubmitting: loadingSelector(state),
  };
};

const mapDispatchToProps = {
  changeBillingType,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

interface OwnProps {
  billingType?: string;
  companyId?: string;
  onCancel: () => void;
  onSuccess: (billingType: string) => void;
}

type Props = WithApolloClient<ConnectedProps<typeof connector> & OwnProps>;

class ChangeBillingTypeForm extends React.PureComponent<Props> {
  _onSubmit = async (values: FormValues) => {
    const result = await this.props.changeBillingType(
      this.props.client as ApolloClient<object>,
      {
        // companyId prop is optional (string | undefined); the mutation var is
        // required. Non-null assertion preserves the original runtime contract.
        companyId: this.props.companyId!,
        billingType: values.billingType,
      }
    );

    if (result.success) {
      this.props.onSuccess(values.billingType);
      toast.success("Billing type changed successfully.");
    }
  };

  render() {
    return (
      <div>
        <p>
          Changing the billing type for an employer will require them to agree
          to a new order form.
        </p>
        {this.props.billingType === STRIPE && (
          <p>
            Changing the billing type for this employer will cancel their
            existing stripe subscription, if it exists.
          </p>
        )}
        <Formik<FormValues>
          onSubmit={this._onSubmit}
          enableReinitialize={true}
          initialValues={{
            // @ts-expect-error billingType prop is optional (string | undefined) per the
            // original PropType; Formik<FormValues> wants string. Preserve the loose
            // runtime contract rather than tighten the prop.
            billingType: this.props.billingType,
          }}
        >
          {({ handleSubmit, handleChange, values }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group as={Col} sm={6} controlId="formBasicBillingType">
                <Form.Label>Current Billing Type</Form.Label>
                <Form.Select
                  name={"billingTypeCurrent"}
                  onChange={handleChange}
                  disabled
                  value={this.props.billingType}
                >
                  <option value={this.props.billingType}>
                    {this.props.billingType}
                  </option>
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} sm={6} controlId="formBasicBillingTypee">
                <Form.Label>New Billing Type</Form.Label>
                <Form.Select
                  name={"billingType"}
                  onChange={handleChange}
                  value={values.billingType}
                >
                  {map(providers, (provider) => (
                    <option value={provider}>{provider}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              {this.props.error && (
                <Alert type="error" msg={this.props.error} />
              )}
              <div className="submit-row">
                <span className="cancel-btn">
                  <Button
                    btnLabel="Cancel"
                    name="cancel"
                    color="cancel"
                    type="button"
                    onClick={() => {
                      this.props.onCancel();
                    }}
                  />
                </span>
                <Button
                  btnLabel="Change Billing Type"
                  name="submit"
                  withArrow={true}
                  disabled={values.billingType === this.props.billingType}
                  loading={this.props.isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default connector(withApollo<Props>(ChangeBillingTypeForm));
